import React, { useEffect, useState } from 'react';
import { Save, Search, Loader2, CheckCircle2, LocateFixed } from 'lucide-react';
import { Input } from '../ui/Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/Select';
import LocationAutocomplete from '../ui/LocationAutocomplete';
import { PercentSlider } from './BookingFormControls';
import DateTimePicker from './DateTimePicker';
import CommandCenterHospitalField from './CommandCenterHospitalField';
import MultiSelectCheckboxDropdown from '../ui/MultiSelectCheckboxDropdown';
import commandCenterService from '../../services/commandCenterService';
import placesService from '../../services/placesService';
import PatientLocationSharePopup from './PatientLocationSharePopup';

// const SERVICE_TYPES = ['Discharge', 'Weekly Standby', 'Monthly Standby', 'Event', 'Others']; // commented out — Service Type field disabled for now, not required

const PARTNER_HOSPITAL_SOURCE = 'Partner Hospital';

const FieldLabel = ({ children, required }) => (
  <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const FieldHint = ({ children }) => (
  <p className="text-xs text-gray-400 mt-1">{children}</p>
);

// Small icon beside "Pick on Map": texts the patient a link whose browser geolocation fills the field
const RequestLocationButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title="Request patient location"
    aria-label="Request patient location"
    className="text-xs text-blue-600 hover:text-blue-700 flex items-center font-medium transition-colors"
  >
    <LocateFixed className="h-3.5 w-3.5" />
  </button>
);

const BLANK_FORM_DATA = {
  hospital: { id: null, name: '' },
  name: '',
  phone: '',
  phone2: '',
  age: '',
  gender: '',
  height: '',
  weight: '',
  pickupText: '',
  dropoffText: '',
  scheduleTime: '',
  patientCondition: '',
  serviceType: '',
  source: '',
  partnerShare: 50,
  requirements: [],
  otherRequirement: '',
  vehicleTypes: [],
};

const CommandCenterBookingForm = ({
  initialFormData,
  onPickupCoordinatesChange,
  onDropoffCoordinatesChange,
  distanceKm,
  durationMin,
  isSameLocation: isSameLocationProp = false,
  priceFields,
  onPriceFieldChange,
  calculatingPrice,
  onVehicleTypeChange,
  onAction,
  onError,
  onSaveLater,
  onSearchAmbulance,
  onFormDataChange,
  searching,
  savingLater,
  isEditMode = false,
  isActiveTrip = false,
  tripStage = null,
  onUpdateBooking,
  updating = false,
  bookingId = null,
  onLocationShareIdChange,
}) => {
  // Lazy initializer, not a sync effect — the parent only mounts this
  // component (via a `key` keyed to the booking being edited, forcing a
  // clean remount) once initialFormData is actually ready, so it's correct
  // on this component's one and only relevant mount.
  const [formData, setFormData] = useState(() => initialFormData || BLANK_FORM_DATA);

  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  const isSameText = Boolean(
    formData.pickupText?.trim() &&
    formData.dropoffText?.trim() &&
    formData.pickupText.trim().toLowerCase() === formData.dropoffText.trim().toLowerCase()
  );
  const isSameLocation = Boolean(isSameLocationProp || isSameText);

  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
  const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(true);

  useEffect(() => {
    let cancelled = false;
    commandCenterService.getAmbulanceTypes()
      .then((result) => {
        if (cancelled) return;
        const allTypes = Array.isArray(result.data) ? result.data : [];
        setVehicleTypeOptions(allTypes);
      })
      .catch((error) => {
        if (!cancelled) onError?.(error.message || 'Failed to load ambulance types');
      })
      .finally(() => {
        if (!cancelled) setLoadingVehicleTypes(false);
      });
    return () => { cancelled = true; };
  }, [onError]);

  const [requirementOptions, setRequirementOptions] = useState([]);
  const [loadingRequirements, setLoadingRequirements] = useState(true);

  useEffect(() => {
    let cancelled = false;
    commandCenterService.getRequirements()
      .then((result) => {
        if (cancelled) return;
        const allOptions = Array.isArray(result.data) ? result.data : [];
        setRequirementOptions(allOptions);
      })
      .catch((error) => {
        if (!cancelled) onError?.(error.message || 'Failed to load requirement options');
      })
      .finally(() => {
        if (!cancelled) setLoadingRequirements(false);
      });
    return () => { cancelled = true; };
  }, [onError]);

  const [sourceOptions, setSourceOptions] = useState([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [patientConditionOptions, setPatientConditionOptions] = useState([]);
  const [loadingPatientConditions, setLoadingPatientConditions] = useState(true);

  useEffect(() => {
    let cancelled = false;
    commandCenterService.getSources()
      .then((result) => {
        if (cancelled) return;
        const options = Array.isArray(result.data) ? result.data : [];
        setSourceOptions(options);
      })
      .catch((error) => {
        if (!cancelled) onError?.(error.message || 'Failed to load source options');
      })
      .finally(() => {
        if (!cancelled) setLoadingSources(false);
      });
    return () => { cancelled = true; };
  }, [onError]);

  useEffect(() => {
    let cancelled = false;
    commandCenterService.getPatientConditions()
      .then((result) => {
        if (cancelled) return;
        const options = Array.isArray(result.data) ? result.data : [];
        setPatientConditionOptions(options);
      })
      .catch((error) => {
        if (!cancelled) onError?.(error.message || 'Failed to load patient condition options');
      })
      .finally(() => {
        if (!cancelled) setLoadingPatientConditions(false);
      });
    return () => { cancelled = true; };
  }, [onError]);

  const updateField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  // Patient location link: one share per booking form, usable for either the pickup or the drop-off field
  const [locationPopupTarget, setLocationPopupTarget] = useState(null); // null | 'pickup' | 'dropoff'
  const [locationShareId, setLocationShareId] = useState(null);

  const handleLocationShareChange = (shareId) => {
    setLocationShareId(shareId);
    onLocationShareIdChange?.(shareId);
  };

  // Called when the agent confirms the received coordinates. Reverse-geocodes them into an address and sets the
  // field exactly as picking a place from the map would; the coordinates the agent entered are only replaced here.
  const handleUsePatientLocation = async (target, { latitude, longitude }) => {
    let name = `${Number(latitude).toFixed(4)}, ${Number(longitude).toFixed(4)}`;
    let address = name;
    let placeId = null;
    try {
      const response = await placesService.reverseGeocode(latitude, longitude);
      if (response?.success && response.data?.formattedAddress) {
        address = response.data.formattedAddress;
        name = address.split(',').map((part) => part.trim()).slice(0, 3).join(', ');
        placeId = response.data.placeId || null;
      }
    } catch (_) { /* keep the coordinate text as the address */ }

    updateField(target === 'dropoff' ? 'dropoffText' : 'pickupText', name);
    const location = { name, address, latitude, longitude, placeId };
    if (target === 'dropoff') onDropoffCoordinatesChange?.(location);
    else onPickupCoordinatesChange?.(location);
  };

  const handleVehicleTypesChange = (updatedList) => {
    setFormData((prev) => {
      onVehicleTypeChange?.(updatedList);
      return { ...prev, vehicleTypes: updatedList };
    });
  };

  const handleRequirementsChange = (updatedList) => {
    setFormData((prev) => ({
      ...prev,
      requirements: updatedList,
    }));
  };

  const toggleVehicleType = (typeName) => {
    setFormData((prev) => {
      const currentList = Array.isArray(prev.vehicleTypes) ? prev.vehicleTypes : [];
      const updatedList = currentList.includes(typeName)
        ? currentList.filter((t) => t !== typeName)
        : [...currentList, typeName];
      onVehicleTypeChange?.(updatedList);
      return { ...prev, vehicleTypes: updatedList };
    });
  };

  const toggleRequirement = (item) => {
    setFormData((prev) => ({
      ...prev,
      requirements: prev.requirements.includes(item)
        ? prev.requirements.filter((r) => r !== item)
        : [...prev.requirements, item],
    }));
  };

  return (
    <div className="bg-white rounded-2xl shadow p-4 sm:p-5 space-y-4 text-sm">
      {/* Row 1: Name * | Phone 1 * | Phone 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <FieldLabel required>Name</FieldLabel>
          <Input
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Enter patient name"
          />
        </div>
        <div>
          <FieldLabel required>Phone 1</FieldLabel>
          <Input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            value={formData.phone}
            onChange={(e) => updateField('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="Enter mobile number"
          />
        </div>
        <div>
          <FieldLabel>Phone 2</FieldLabel>
          <Input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            value={formData.phone2}
            onChange={(e) => updateField('phone2', e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="Alternate mobile number"
          />
        </div>
      </div>

      {/* Row 2: Age | Gender | Height (cm) | Weight (kg) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <FieldLabel>Age</FieldLabel>
          <Input
            type="number"
            min="0"
            max="120"
            value={formData.age}
            onChange={(e) => updateField('age', e.target.value)}
            placeholder="Age in years"
          />
        </div>
        <div>
          <FieldLabel>Gender</FieldLabel>
          <Select
            value={formData.gender}
            onValueChange={(v) => updateField('gender', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MALE">Male</SelectItem>
              <SelectItem value="FEMALE">Female</SelectItem>
              <SelectItem value="OTHER">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Height (cm)</FieldLabel>
          <Input
            type="number"
            min="0"
            max="300"
            value={formData.height}
            onChange={(e) => updateField('height', e.target.value)}
            placeholder="Height in cm"
          />
        </div>
        <div>
          <FieldLabel>Weight (kg)</FieldLabel>
          <Input
            type="number"
            min="0"
            max="500"
            step="0.1"
            value={formData.weight}
            onChange={(e) => updateField('weight', e.target.value)}
            placeholder="Weight in kg"
          />
        </div>
      </div>

      {/* Row 3: Patient Condition * | Partner Hospital */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel required>Patient Condition</FieldLabel>
          <Select
            value={formData.patientCondition}
            onValueChange={(v) => updateField('patientCondition', v)}
            disabled={loadingPatientConditions}
          >
            <SelectTrigger>
              <SelectValue placeholder={loadingPatientConditions ? 'Loading conditions...' : 'Select patient condition'} />
            </SelectTrigger>
            <SelectContent>
              {patientConditionOptions.map((condition) => (
                <SelectItem key={condition.id} value={condition.name}>{condition.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <CommandCenterHospitalField
            value={formData.hospital}
            onChange={(v) => updateField('hospital', v)}
            onAction={onAction}
            onError={onError}
            label="Partner Hospital"
            placeholder="Select partner hospital"
          />
        </div>
      </div>

      {/* Row 4: Pickup Location * | Drop off Location * */}
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between min-h-[22px] mb-1.5 flex-wrap gap-1">
              <div className="flex items-center gap-1.5">
                <label className="text-xs font-semibold text-gray-500">Pickup Location<span className="text-red-500 ml-0.5">*</span></label>
                {isActiveTrip && (
                  <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-medium border border-amber-200">
                    Locked during active trip
                  </span>
                )}
              </div>
              {(distanceKm != null || durationMin != null) && (
                <div className="flex items-center gap-1.5">
                  {distanceKm != null && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
                      {distanceKm.toFixed(1)} km
                    </span>
                  )}
                  {durationMin != null && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700">
                      {Math.round(durationMin)} min
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className={isActiveTrip ? 'pointer-events-none opacity-80' : ''}>
              <LocationAutocomplete
                value={formData.pickupText}
                onChange={(e) => updateField('pickupText', e.target.value)}
                onLocationSelect={(location) => onPickupCoordinatesChange?.(location)}
                placeholder="Search pickup location..."
                showMapPicker={!isActiveTrip}
                /* showCurrentLocation - commented out as struck through in reference design */
                showSelectedStatus={false}
                mapPickerTitle="Select Pickup Location"
                pinColor="#10B981"
                extraActions={!isActiveTrip && <RequestLocationButton onClick={() => setLocationPopupTarget('pickup')} />}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between min-h-[22px] mb-1.5">
              <label className="text-xs font-semibold text-gray-500">Drop off Location<span className="text-red-500 ml-0.5">*</span></label>
              {isSameLocation ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
                  Same as pickup
                </span>
              ) : (
                (distanceKm != null || durationMin != null) && (
                  <div className="flex items-center gap-1.5">
                    {distanceKm != null && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
                        {distanceKm.toFixed(1)} km
                      </span>
                    )}
                    {durationMin != null && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700">
                        {Math.round(durationMin)} min
                      </span>
                    )}
                  </div>
                )
              )}
            </div>
            <LocationAutocomplete
              value={formData.dropoffText}
              onChange={(e) => updateField('dropoffText', e.target.value)}
              onLocationSelect={(location) => onDropoffCoordinatesChange?.(location)}
              placeholder="Search drop-off location..."
              showMapPicker
              /* showCurrentLocation - commented out as struck through in reference design */
              showSelectedStatus={false}
              mapPickerTitle="Select Drop-off Location"
              pinColor="#EF4444"
              suggestionsPlacement="below"
              extraActions={<RequestLocationButton onClick={() => setLocationPopupTarget('dropoff')} />}
            />
            {isSameLocation && (
              <p className="text-xs text-red-600 mt-1 font-medium">
                Drop-off location cannot be the same as pickup location.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Row 5: Vehicle Type | Requirements */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel>Vehicle Type</FieldLabel>
          <MultiSelectCheckboxDropdown
            id="booking-vehicle-type-select"
            options={vehicleTypeOptions}
            value={formData.vehicleTypes || []}
            onChange={handleVehicleTypesChange}
            placeholder="Select vehicle type(s)..."
            loading={loadingVehicleTypes}
            loadingText="Loading vehicle types…"
            emptyText="No vehicle types configured"
            searchPlaceholder="Search vehicle types…"
          />
        </div>
        <div>
          <FieldLabel>Requirements</FieldLabel>
          <MultiSelectCheckboxDropdown
            id="booking-requirements-select"
            options={requirementOptions}
            value={formData.requirements || []}
            onChange={handleRequirementsChange}
            placeholder="Select requirement(s)..."
            loading={loadingRequirements}
            loadingText="Loading requirements…"
            emptyText="No requirements configured"
            searchPlaceholder="Search requirements…"
          />
        </div>
      </div>

      {/* Row 6: Remarks */}
      <div>
        <FieldLabel>Remarks</FieldLabel>
        <textarea
          value={formData.otherRequirement}
          onChange={(e) => updateField('otherRequirement', e.target.value)}
          placeholder="Enter remarks or additional requirements..."
          rows={2}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-xs sm:text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
        />
      </div>

      {/* Service Type — commented out per user request (not present in reference design) */}
      {/* <div>
        <FieldLabel>Service Type</FieldLabel>
        <Select value={formData.serviceType} onValueChange={(v) => updateField('serviceType', v)}>
          <SelectTrigger>
            <SelectValue placeholder="Choose an option..." />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_TYPES.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div> */}

      {/* Row 7: Source | Schedule Time */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel>Source</FieldLabel>
          <Select
            value={formData.source}
            onValueChange={(v) => updateField('source', v)}
            disabled={loadingSources}
          >
            <SelectTrigger>
              <SelectValue placeholder={loadingSources ? 'Loading sources...' : 'Select booking source'} />
            </SelectTrigger>
            <SelectContent>
              {sourceOptions.map((source) => (
                <SelectItem key={source.id} value={source.name}>{source.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <FieldLabel>Schedule Time</FieldLabel>
            {isActiveTrip && (
              <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium border border-blue-200">
                Trip in progress {tripStage ? `(${tripStage})` : ''}
              </span>
            )}
          </div>
          <div className={isActiveTrip ? 'pointer-events-none opacity-80' : ''}>
            <DateTimePicker
              value={formData.scheduleTime}
              onChange={(v) => updateField('scheduleTime', v)}
            />
          </div>
        </div>
      </div>

      {/* Row 8: Pricing — Fleet Operator / Platform Fee / Operator Service Fee / Total */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* System Price commented out per request
        <div>
          <FieldLabel>System Price</FieldLabel>
          <div className="relative">
            <Input
              type="number"
              min="0"
              step="any"
              value={priceFields.systemPrice}
              disabled
              placeholder={calculatingPrice ? 'Calculating…' : 'Auto-calculated'}
              className={calculatingPrice ? 'pr-7' : undefined}
            />
            {calculatingPrice && (
              <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin absolute right-2 top-1/2 -translate-y-1/2" />
            )}
          </div>
        </div>
        */}
        <div>
          <FieldLabel>Fleet Operator Price</FieldLabel>
          <Input
            type="number"
            min="0"
            step="any"
            value={priceFields.fleetOperatorPrice ?? priceFields.offeredPrice ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onPriceFieldChange('fleetOperatorPrice', val);
              const fo = val !== '' ? Number(val) : 0;
              const ps = Number(priceFields.platformServiceFee ?? priceFields.agreedPrice ?? 0);
              const fos = Number(priceFields.fleetOperatorServiceFee ?? priceFields.partnerPrice ?? 0);
              const sum = fo + ps + fos;
              onPriceFieldChange('totalPrice', sum > 0 ? sum : '');
              // Auto-sync Partner Price with Partner Share Split % disabled per request (logic preserved for future use):
              // onPriceFieldChange('partnerPrice', val !== '' ? Math.round(Number(val) * formData.partnerShare / 100) : '');
            }}
            placeholder="Enter operator price"
          />
        </div>
        <div>
          <FieldLabel>Platform Service Fee</FieldLabel>
          <Input
            type="number"
            min="0"
            step="any"
            value={priceFields.platformServiceFee ?? priceFields.agreedPrice ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onPriceFieldChange('platformServiceFee', val);
              const ps = val !== '' ? Number(val) : 0;
              const fo = Number(priceFields.fleetOperatorPrice ?? priceFields.offeredPrice ?? 0);
              const fos = Number(priceFields.fleetOperatorServiceFee ?? priceFields.partnerPrice ?? 0);
              const sum = fo + ps + fos;
              onPriceFieldChange('totalPrice', sum > 0 ? sum : '');
            }}
            placeholder="Enter platform fee"
          />
        </div>
        <div>
          <FieldLabel>Operator Service Fee</FieldLabel>
          <Input
            type="number"
            min="0"
            step="any"
            value={priceFields.fleetOperatorServiceFee ?? priceFields.partnerPrice ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onPriceFieldChange('fleetOperatorServiceFee', val);
              const fos = val !== '' ? Number(val) : 0;
              const fo = Number(priceFields.fleetOperatorPrice ?? priceFields.offeredPrice ?? 0);
              const ps = Number(priceFields.platformServiceFee ?? priceFields.agreedPrice ?? 0);
              const sum = fo + ps + fos;
              onPriceFieldChange('totalPrice', sum > 0 ? sum : '');
            }}
            placeholder="Enter service fee"
          />
        </div>
        <div>
          <FieldLabel>Total</FieldLabel>
          <Input
            type="number"
            min="0"
            step="any"
            value={priceFields.totalPrice ?? ''}
            onChange={(e) => onPriceFieldChange('totalPrice', e.target.value)}
            placeholder="Total price"
          />
        </div>
      </div>

      {/* Partner share split — commented out per user request (not shown in reference design, preserved for future use) */}
      {/* <div>
        <div className="flex items-center justify-between mb-2">
          <FieldLabel>Partner Share Split</FieldLabel>
          <span className="text-sm font-semibold text-primary">{formData.partnerShare}%</span>
        </div>
        <PercentSlider
          value={formData.partnerShare}
          onChange={(v) => {
            updateField('partnerShare', v);
            if (priceFields.agreedPrice !== '' && priceFields.agreedPrice != null) {
              onPriceFieldChange('partnerPrice', Math.round(Number(priceFields.agreedPrice) * v / 100));
            }
          }}
        />
      </div> */}

      {/* Row 9: Action Buttons (Save Later / Update + Search Ambulance) */}
      <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
        {!isEditMode && (
          <button
            type="button"
            onClick={() => {
              if (isSameLocation) {
                onError?.('Pickup and drop-off locations cannot be the same. Please select a different drop-off location.');
                return;
              }
              onSaveLater?.({ ...formData, ...priceFields });
            }}
            disabled={savingLater || searching || updating}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 text-xs font-semibold shadow-2xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {savingLater ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-gray-500" />}
            <span>{savingLater ? 'Saving…' : 'Save Later'}</span>
          </button>
        )}

        {isEditMode && (
          <button
            type="button"
            onClick={() => {
              if (isSameLocation) {
                onError?.('Pickup and drop-off locations cannot be the same. Please select a different drop-off location.');
                return;
              }
              onUpdateBooking?.({ ...formData, ...priceFields });
            }}
            disabled={updating || searching}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-xs hover:shadow transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            <span>{updating ? 'Updating…' : 'Update'}</span>
          </button>
        )}

        {/* Search Ambulance: shown when creating new, or editing pending/scheduled trips. Hidden during active trips to avoid disrupting live tracking */}
        {(!isEditMode || !isActiveTrip) && (
          <button
            type="button"
            onClick={() => {
              if (isSameLocation) {
                onError?.('Pickup and drop-off locations cannot be the same. Please select a different drop-off location.');
                return;
              }
              onSearchAmbulance?.({ ...formData, ...priceFields });
            }}
            disabled={searching || savingLater || updating}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-xs hover:shadow transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span>{searching ? 'Searching…' : 'Search Ambulance'}</span>
          </button>
        )}
      </div>

      {locationPopupTarget && (
        <PatientLocationSharePopup
          target={locationPopupTarget}
          phone={formData.phone}
          patientName={formData.name}
          bookingId={bookingId}
          existingShareId={locationShareId}
          onShareChange={handleLocationShareChange}
          onUseLocation={(location) => handleUsePatientLocation(locationPopupTarget, location)}
          onClose={() => setLocationPopupTarget(null)}
        />
      )}
    </div>
  );
};

export default CommandCenterBookingForm;
